// Turns the rendered report table into a preview of the columns being composed
// in the inline column editor: the report's own data columns are collapsed away
// (the row-label column stays) and cells standing for the configured columns are
// appended in their place. Headers only — no figures are rendered here, they are
// computed when the columns are applied.

import Tooltip from '@app/setup/tooltip/uikit-tooltip'
import {
  FORECAST_CELL_ATTRIBUTE,
  forecastCellIn,
} from '@app/application/report/forecast/column'

import type { PreviewColumn, PreviewHeader, PreviewResponse } from './types'

import { useColumnEditorI18n } from './use-column-editor-i18n'

// Set on the table while it is previewing. The report's own columns are hidden
// from a single stylesheet rule keyed off this class (see reports.scss) rather
// than cell by cell: a large report is tens of thousands of cells, and toggling
// one class leaves the work to the browser.
const PREVIEWING_CLASS = 'report-column-preview'
const CELL_CLASS = 'report-column-preview__cell'

// Set while a preview is on its way: the report's own figures go, headers aside,
// and go out of the layout with it. A column is as wide as the widest thing in
// it, so with the figures gone the table already measures what it will measure
// once the preview is in — see reports.scss. The columns settle as the drawer
// opens, and the preview landing moves nothing.
const PENDING_CLASS = 'report-column-preview-pending'

// A period the report would drop, previewed greyed out.
const HIDDEN_CELL_CLASS = 'report-column-preview__cell--hidden'

// The rows the preview brings under the dates: the value labels, and the
// dimensions an analytical column splits across. Marks them as the preview's to
// take away again, and keeps them out of the rule that hides the report's own
// subheader rows (see reports.scss).
const PREVIEW_ROW_CLASS = 'report-column-preview__row'

// The report's own stacking classes, mirroring `report_table_cell` in
// reports_helper.rb: header cells are sticky and have to keep painting over the
// body cells scrolling under them.
const HEADER_CLASS = 'report__table--level-3'
const BODY_CLASS = 'report__table--level-1'

// What the report renders a spacer column with — a blank column without the
// borders that separate real ones.
const SPACER_CLASS = 'report-spacer'

// One period of one configured column: the date, what the columns under it show,
// and the dimensions it splits across. The report renders one column for every
// pairing of the two, so `columnCount` of them.
//
// What the server said about the period plus the one thing this module adds — a
// spacer is a column with no period behind it. Intersected rather than restated,
// so a field added to PreviewHeader has to be accounted for here instead of
// being dropped on the way through `flatten`. Both axes are required here even
// where the response leaves one out: `flatten` settles that, so nothing
// downstream has to ask.
type PreviewGroup = PreviewHeader & { dimensions: string[]; spacer: boolean }

// Tooltips are attached to the header cells of dropped periods. They own DOM of
// their own outside the table, so they are disposed when the cells go rather
// than left pointing at detached elements.
let tooltips: Tooltip[] = []

export const findReportTable = (): HTMLTableElement | null =>
  document.querySelector<HTMLTableElement>('.report-table-container table')

// Whether this table is currently showing a preview instead of its own columns.
// Asked of the table rather than remembered by the caller: a preview belongs to
// the table it was rendered into, and the table is replaced out from under the
// column editor often enough (the frame reloads on a period set change, on a
// finished async render) that a flag beside it is a second answer to keep in
// step with this one.
export const isPreviewing = (table: HTMLTableElement): boolean =>
  table.classList.contains(PREVIEWING_CLASS)

// Empties the report's own figures for as long as it takes the first preview to
// arrive, leaving the table laid out the way the preview will find it. The headers
// keep naming their periods until the preview's own headers replace them.
//
// Takes a missing table for the same reason `restoreReportColumns` does: the
// table can be gone (the frame reloads and replaces it), and there is nothing to
// empty when it is.
export const blankReportColumns = (table: HTMLTableElement | null): void => {
  table?.classList.add(PENDING_CLASS)
}

// Hides the report's own columns. Only ever done together with putting preview
// cells in their place, so the table is never left empty: it holds its real
// columns until there is something to show instead of them.
const collapseReportColumns = (table: HTMLTableElement): void => {
  table.classList.remove(PENDING_CLASS)
  table.classList.add(PREVIEWING_CLASS)
}

// Takes a missing table: the tooltips are this module's to clean up either way,
// and the table can be gone by the time the drawer closes (the frame reloads and
// replaces it).
export const restoreReportColumns = (table: HTMLTableElement | null): void => {
  disposeTooltips()
  if (!table) return

  table.classList.remove(PREVIEWING_CLASS, PENDING_CLASS)
  removePreviewCells(table)
}

export const renderPreviewColumns = (
  table: HTMLTableElement,
  { columns, analytical }: PreviewResponse
): void => {
  // Always there: the report's data matrix pushes its header row before anything
  // else (Report::TemplateLogic#data_matrix). A table without one is a bug worth
  // hearing about, so this throws rather than bailing out — the caller gives the
  // report its own columns back and reports it.
  //
  // Thrown before the table is touched, or that recovery has nothing to work
  // with: the caller only restores a table it hasn't already marked as
  // previewing, and a half-collapsed table would keep the report's own columns
  // hidden with no preview cells to show in their place.
  const headerRow = table.querySelector<HTMLTableRowElement>('thead tr')
  if (!headerRow) throw new Error('No header row to render the preview into')

  removePreviewCells(table)
  collapseReportColumns(table)

  const groups = flatten(columns)

  insertPreviewCells(headerRow, buildDateCells(groups))

  // Under the dates, in the order the report itself pushes them: what each column
  // shows, then which dimension it shows it for.
  const subheaderRows: HTMLTableRowElement[] = []

  if (groups.some((group) => group.sublabels.length > 0)) {
    subheaderRows.push(buildSubheaderRow(headerRow, buildValueCells(groups)))
  }

  if (analytical) {
    subheaderRows.push(
      buildSubheaderRow(headerRow, buildDimensionCells(groups))
    )
  }

  headerRow.after(...subheaderRows)

  // Built once and cloned per row: a report can run to thousands of rows, and
  // each one needs the same blank cells.
  const bodyCells = buildBlankCells(groups)
  table
    .querySelectorAll<HTMLTableRowElement>('tbody tr')
    .forEach((row) =>
      insertPreviewCells(row, bodyCells.cloneNode(true) as DocumentFragment)
    )
}

// The report's own columns are the preview's to replace, but a forecast column is
// not one of them: it is unsaved work the user has on the page, so the preview
// leaves it visible (reports.scss) and where it is — at the end, which is the only
// place it goes. So the preview's cells go in front of it rather than after it.
const insertPreviewCells = (
  row: HTMLTableRowElement,
  cells: DocumentFragment
): void => {
  row.insertBefore(cells, forecastCellIn(row))
}

const removePreviewCells = (table: HTMLTableElement): void => {
  disposeTooltips()

  table.querySelectorAll(`.${CELL_CLASS}`).forEach((cell) => cell.remove())
  table.querySelectorAll(`.${PREVIEW_ROW_CLASS}`).forEach((row) => row.remove())
}

// A row under the dates, holding the cells it is given. The report renders these
// itself as soon as its own columns call for them (a budget set, adjustments,
// mixed relative periods, an analytical column) and leaves them out otherwise —
// but the columns being composed in the drawer are not the report's, and may need
// a row where the report's columns don't. So the preview always brings its own
// and the report's are hidden for as long as it is up (see reports.scss).
//
// None of their design is reproduced here. The report styles a subheader by where
// it sits — `.report thead tr:nth-child(n + 2)` — so a row in that position
// already looks the part, and the row and its label cell are shallow-cloned off
// the header row above, which carries the same classes `reports_helper` gives the
// report's own subheader row. Restyle either in Ruby and this follows.
const buildSubheaderRow = (
  headerRow: HTMLTableRowElement,
  cells: DocumentFragment
): HTMLTableRowElement => {
  const row = headerRow.cloneNode(false) as HTMLTableRowElement
  row.classList.add(PREVIEW_ROW_CLASS)

  // Attributes only, no children: the header row's label cell holds the report
  // name, the period-set picker and the collapse controls, and a subheader row's
  // is empty.
  row.appendChild(headerRow.cells[0].cloneNode(false))
  row.appendChild(cells)

  // The forecast column, when there is one, is named in the row above and has
  // nothing to say in this one — but the row still needs a cell under it, or the
  // subheader block stops short of the column and leaves a gap where the rest of
  // the header is coloured.
  if (forecastCellIn(headerRow)) row.appendChild(buildBlankHeaderCell())

  return row
}

// Marked as the forecast's own, or clearing walks past it and leaves a stray cell.
const buildBlankHeaderCell = (): HTMLTableCellElement => {
  const cell = buildCell(
    'th',
    { label: '', sublabels: [], dimensions: [], hidden: false, spacer: false },
    {}
  )
  cell.setAttribute(FORECAST_CELL_ATTRIBUTE, '')

  return cell
}

const disposeTooltips = (): void => {
  tooltips.forEach((tooltip) => tooltip.dispose())
  tooltips = []
}

// Flattens the configured columns into the periods the report would render for
// them: one configured column can span several periods (a year's worth of
// monthly periods is a period each), a spacer is a single blank column.
const flatten = (columns: PreviewColumn[]): PreviewGroup[] =>
  columns.flatMap((column): PreviewGroup[] =>
    column.spacer
      ? [
          {
            label: '',
            sublabels: [],
            dimensions: [],
            hidden: false,
            spacer: true,
          },
        ]
      : column.headers.map((header) => ({
          ...header,
          // A response from a pod that predates the dimension axis reads as a
          // report with no analytical column, rather than throwing out of a
          // render that has already collapsed the report's own columns — the one
          // state the caller cannot recover from.
          dimensions: header.dimensions ?? [],
          spacer: false,
        }))
  )

// The two axes a period expands along: the values it shows, and the dimensions it
// splits across. Each subheader row's cells span the other axis, so these are also
// the two colspans.
const valueCount = (group: PreviewGroup): number =>
  Math.max(1, group.sublabels.length)

const dimensionCount = (group: PreviewGroup): number =>
  Math.max(1, group.dimensions.length)

// How many columns the report renders for one period: one per value it shows, per
// dimension it splits across. An analytical column showing actuals for three
// dimensions is three columns; showing actuals *and* a budget for them is six.
const columnCount = (group: PreviewGroup): number =>
  valueCount(group) * dimensionCount(group)

// The dates. One cell per period, spanning the columns that period renders as.
const buildDateCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    fragment.appendChild(
      buildCell('th', group, {
        text: group.label,
        span: columnCount(group),
        explain: true,
      })
    )
  })

  return fragment
}

// The value row: one cell per value the period shows, each spanning the dimensions
// it shows it for.
const buildValueCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    const span = dimensionCount(group)

    labelsOrBlank(group.sublabels).forEach((sublabel) => {
      fragment.appendChild(buildCell('th', group, { text: sublabel, span }))
    })
  })

  return fragment
}

// The dimension row: one cell per dimension the period splits across, each
// spanning the values shown for it. A column that names no dimension while another
// one does — a difference column, a spacer — takes a blank cell here.
const buildDimensionCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    const span = valueCount(group)

    labelsOrBlank(group.dimensions).forEach((dimension) => {
      fragment.appendChild(buildCell('th', group, { text: dimension, span }))
    })
  })

  return fragment
}

// An axis a period says nothing on still takes one cell, blank: a row that came up
// short would shift every column after it.
const labelsOrBlank = (labels: string[]): string[] =>
  labels.length > 0 ? labels : ['']

// A body row's worth: no figures are previewed, but every column needs its cell
// for the row to keep its shape.
const buildBlankCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    for (let index = 0; index < columnCount(group); index += 1) {
      fragment.appendChild(buildCell('td', group, {}))
    }
  })

  return fragment
}

// `explain` marks the cell that names the period: the one worth telling the user
// why the report won't show it.
const buildCell = (
  tag: 'th' | 'td',
  group: PreviewGroup,
  { text, span, explain }: { text?: string; span?: number; explain?: boolean }
): HTMLTableCellElement => {
  const cell = document.createElement(tag)
  cell.classList.add(CELL_CLASS, tag === 'th' ? HEADER_CLASS : BODY_CLASS)
  if (group.spacer) cell.classList.add(SPACER_CLASS)
  if (group.hidden) cell.classList.add(HIDDEN_CELL_CLASS)
  if (span && span > 1) cell.colSpan = span
  if (text) setMultilineText(cell, text)
  if (group.hidden && explain) explainHiddenPeriod(cell)

  return cell
}

const explainHiddenPeriod = (cell: HTMLElement): void => {
  const t = useColumnEditorI18n()
  const reason = t('hidden_period_tooltip')

  // The tooltip only answers a pointer. Greyed out is all the cell would say to a
  // screen reader otherwise, so the reason goes in it as text only that reads —
  // alongside the date rather than in place of it, which is what an `aria-label`
  // on the cell would do.
  const note = document.createElement('span')
  note.className = 'tw-sr-only'
  note.textContent = reason
  cell.appendChild(note)

  tooltips.push(
    new Tooltip(cell, {
      title: reason,
      placement: 'bottom',
      delay: 100,
    })
  )
}

// A difference column's header names both of its periods on separate lines.
const setMultilineText = (cell: HTMLElement, text: string): void => {
  text.split('\n').forEach((line, index) => {
    if (index > 0) cell.appendChild(document.createElement('br'))
    cell.appendChild(document.createTextNode(line))
  })
}
